import * as config from '@terraframing-suite/rx-config';
import { Box, Grid, MenuItem } from '@mui/material';
import { range } from 'lodash-es';
import { useState, useEffect } from 'react';
import { Select, NumericInput } from './form-controls.jsx';
import { Section } from './Section.jsx';
import { useLpkRunContext } from './lpk-run-context.jsx';
import { OtherProductDialog } from './OtherProductDialog.jsx';
import { Radio } from '../../../run-rx/form-controls.jsx';
import { InputsHeader } from '../../../run-rx/InputsHeader.jsx';

const plannedMonthOptions = range(1, 13).map((month) => (
  <MenuItem key={month} value={month}>
    {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
  </MenuItem>
));

const currentYear = new Date().getFullYear();
const plannedYearOptions = range(currentYear, currentYear + 12).map((year) => (
  <MenuItem key={year} value={year}>
    {year}
  </MenuItem>
));

export function NutrientSection({ nutrient, label }) {
  const [isEnteringPercentage, enterPercentage] = useState(false);
  const { getParam, updateParam } = useLpkRunContext();
  const selectedProduct = config[nutrient].products.find(
    (product) => product.id === getParam(`${nutrient}.product`),
  );

  const groupOptions = config[nutrient].groups.map((group) => (
    <MenuItem key={group} value={group}>
      {group}
    </MenuItem>
  ));

  useEffect(() => {
    if (!getParam(`${nutrient}.group`)) updateParam(`${nutrient}.group`, 50);
  }, [getParam, updateParam, nutrient]);

  return (
    <Section>
      <InputsHeader
        title={`${label} Products`}
        subtitle="Products"
        required
        name={`lpk.${nutrient}.product`}
        sx={{ mb: -1, '& p:first-of-type': { mb: 1 } }}
      />

      {isEnteringPercentage && (
        <OtherProductDialog
          product={selectedProduct?.label}
          nutrient={nutrient}
          label={label}
          onClose={() => {
            enterPercentage(false);
            if (!getParam(`${nutrient}.percentage`)) {
              updateParam(`${nutrient}.product`, null);
            }
          }}
        />
      )}

      <Grid container rowSpacing={1} columnSpacing={{ xs: 2, sm: 3 }}>
        {getProducts()}
        <Grid item xs={6}>
          <Select
            required
            label={`${label} Groupings (lb/ac)`}
            name={`${nutrient}.group`}
            options={groupOptions}
          />
        </Grid>
        <Grid item xs={6}>
          <NumericInput
            name={`${nutrient}.otherApps`}
            label={
              label === 'P' ? `Other P₂O₅ Apps. (lbs)` : `Other K₂O Apps. (lbs)`
            }
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <NumericInput
            required
            name={`${nutrient}.yearsToCriticalLevel`}
            label="Years to Critical Level"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <NumericInput
            required
            name={`${nutrient}.croppingYears`}
            label="Num Cropping Years"
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <Select
            required
            label="Planned App Month"
            name={`${nutrient}.applicationMonth`}
            options={plannedMonthOptions}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Select
            required
            label="Planned App Year"
            name={`${nutrient}.applicationYear`}
            options={plannedYearOptions}
          />
        </Grid>
      </Grid>
    </Section>
  );

  function getProducts() {
    return (
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mt: 2,
          }}
        >
          {config[nutrient].products.map((product, i) => {
            const isOther =
              selectedProduct?.id === 'other' && product.id === 'other';

            return (
              <Radio
                sx={{ width: 'auto' }}
                key={i}
                label={product.label}
                subLabel={
                  isOther ? `${getParam(`${nutrient}.percentage`)}%` : ''
                }
                checked={product.id === getParam(`${nutrient}.product`)}
                name={`${nutrient}.product`}
                radio={true}
                color="inherit"
                onChange={(e) => {
                  if (product.id === 'other') {
                    enterPercentage(true);
                  }

                  if (e.target.checked) {
                    updateParam(`${nutrient}.product`, product.id);
                    updateParam(`${nutrient}.percentage`, product.value);
                  }
                }}
                onSubActionClick={
                  isOther ? () => enterPercentage(product) : null
                }
              />
            );
          })}
        </Box>
      </Grid>
    );
  }
}
